import {
  REPORTING_TAB_TYPES,
  REPORTING_VIEW_TYPES,
  type ReportingSortingDirection,
} from "@/pages/reporting_v2/types";
import { translate } from "@/translations/functions/hook";

import type { ReportingTableHeader, WasteColumn } from "../../types";

export const CHECKBOX_COLUMN_ID = "checkbox";
export const NAME_COLUMN_ID = "name";
export const ZONE_COLUMN_ID = "zone";
export const PERIOD_COLUMN_ID = "period";
export const RATE_COLUMN_ID = "recyclingRate";
export const TOTAL_COLUMN_ID = "total";

const FIXED_TOTAL_COLUMN_STYLE = "border-l-2 border-l-[#E4DDDD] pl-[14px]";

export const getReportingTableHeaders = ({
  viewType,
  tabType,
  sortKey,
  sortDirection,
  onSort,
  showZones,
  wasteColumns,
}: {
  viewType: REPORTING_VIEW_TYPES;
  tabType: REPORTING_TAB_TYPES;
  sortKey: string;
  sortDirection: ReportingSortingDirection;
  onSort: (key: string) => void;
  showZones: boolean;
  wasteColumns: WasteColumn[];
}) => {
  const isPortfolioView = viewType === REPORTING_VIEW_TYPES.PORTFOLIO;
  const isIndividualTab = tabType === REPORTING_TAB_TYPES.INDIVIDUAL;

  const sortProps = (id: string) =>
    isIndividualTab
      ? {
          sortable: true,
          isSortingActive: sortKey === id,
          sortingDirection: sortDirection,
          onClick: onSort,
        }
      : {};

  const wasteHeaders: ReportingTableHeader[] = wasteColumns.map((wt) => ({
    id: wt.code,
    label: wt.label,
    width: 132,
    columnStyle: "whitespace-normal leading-[1.3]",
    downstreamHandling: isPortfolioView ? undefined : wt.downstreamHandling,
    ...sortProps(wt.code),
  }));

  const recyclingRateColumn: ReportingTableHeader = {
    id: RATE_COLUMN_ID,
    label: translate("reportingPage:reportingColumnRecyclingRate"),
    width: 116,
    columnStyle: "px-[16px]",
    ...sortProps(RATE_COLUMN_ID),
  };

  const tableHeaders: ReportingTableHeader[] = [];

  if (isIndividualTab) {
    tableHeaders.push(
      {
        id: CHECKBOX_COLUMN_ID,
        isCheckbox: true,
        label: "",
        width: 46,
        isFixedColumn: true,
        columnStyle: "pr-0 pl-[18px]",
      },
      {
        id: NAME_COLUMN_ID,
        label: isPortfolioView
          ? translate("reportingPage:reportingColumnBuilding")
          : translate("reportingPage:reportingColumnTenant"),
        width: 210,
        isFixedColumn: true,
        columnStyle: "text-left pr-[12px] pl-[4px]",
        ...sortProps(NAME_COLUMN_ID),
      },
    );

    if (!isPortfolioView && showZones) {
      tableHeaders.push({
        id: ZONE_COLUMN_ID,
        label: translate("reportingPage:reportingColumnZone"),
        width: 118,
        isFixedColumn: true,
        columnStyle: "text-left",
        ...sortProps(ZONE_COLUMN_ID),
      });
    }

    tableHeaders.push(recyclingRateColumn, ...wasteHeaders, {
      id: TOTAL_COLUMN_ID,
      label: translate("reportingPage:reportingColumnRowTotal"),
      width: 120,
      isFixedColumn: true,
      columnStyle: `${FIXED_TOTAL_COLUMN_STYLE} pr-[18px]`,
      ...sortProps(TOTAL_COLUMN_ID),
    });
  } else {
    tableHeaders.push(
      {
        id: PERIOD_COLUMN_ID,
        label: translate("reportingPage:reportingColumnPeriod"),
        width: 160,
        isFixedColumn: true,
        columnStyle: "text-left pr-[16px] pl-[22px]",
      },
      recyclingRateColumn,
      ...wasteHeaders,
      {
        id: TOTAL_COLUMN_ID,
        label: translate("reportingPage:reportingColumnTotal"),
        width: 120,
        isFixedColumn: true,
        columnStyle: `${FIXED_TOTAL_COLUMN_STYLE} pr-[22px]`,
      },
    );
  }

  return tableHeaders;
};
